import {
  Dropdown,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import { Information } from '@carbon/icons-react';

import type { SourceType } from '../types';
import type { CreateSourceOption } from '../utils';
import styles from './select-source-step.module.scss';

const LABEL = 'Create Package from';

export interface SourceDropdownProps {
  options: CreateSourceOption[];
  selected: SourceType | null;
  onChange: (source: CreateSourceOption['id']) => void;
}

/**
 * "Create Package from": the sources a new package can start from, with an info
 * toggletip describing each. The label and the toggletip sit outside the
 * dropdown's own label, which would otherwise take the toggletip's click.
 *
 * @param {SourceDropdownProps} props - The options, the picked one and the handler
 * @return {JSX.Element} The labelled dropdown
 */
export function SourceDropdown({
  options,
  selected,
  onChange,
}: SourceDropdownProps) {
  return (
    <div className={styles.sourceField}>
      <div className={styles.sourceLabel}>
        <span className={styles.sourceLabelText}>{LABEL}</span>
        <Toggletip align="bottom-start">
          <ToggletipButton label="About the sources">
            <Information />
          </ToggletipButton>
          <ToggletipContent>
            <dl className={styles.sourceTooltip}>
              {options.map((option) => (
                <div key={option.id} className={styles.sourceTooltipItem}>
                  <dt className={styles.sourceTooltipLabel}>{option.label}</dt>
                  <dd>{option.description}</dd>
                  {option.note && (
                    <dd className={styles.sourceTooltipNote}>{option.note}</dd>
                  )}
                </div>
              ))}
            </dl>
          </ToggletipContent>
        </Toggletip>
      </div>
      <Dropdown<CreateSourceOption | null>
        id="package-source"
        titleText={LABEL}
        hideLabel
        label="Select an option"
        items={options}
        itemToString={(item) => item?.label ?? ''}
        // Null, not undefined, while nothing is picked: the dropdown stays controlled.
        selectedItem={options.find((option) => option.id === selected) ?? null}
        onChange={({ selectedItem }) => {
          if (selectedItem) onChange(selectedItem.id);
        }}
        className={styles.sourceDropdown}
        // As tall as the As of Date beside it.
        size="lg"
      />
    </div>
  );
}
